import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical, faAngleDown } from '@fortawesome/free-solid-svg-icons';
import 'bulma/css/bulma.min.css';

import { ScanTypeSlug, ScanType } from '../../scanTypes.ts';
import { BeamlineContext, BeamlineLoadingState } from '../../beamlineProvider.tsx';
import { LoadingBanner, LoadingState } from '../../components/loadingBanner.tsx';
import BeamlineHeader from '../../components/beamlineHeader.tsx';
import DeprecateScanType from './deprecateScanType.tsx';
import DeleteScanType from './deleteScanType.tsx';
import ParameterSettingsTable from './parameterSettingsTable.tsx';
import { InputEditable, ValidationStatus } from '../../components/inputEditable.tsx';
import { updateScanType } from '../../api/api.ts';


const EditScanType: React.FC = () => {

  var { scanTypeSlug } = useParams();
  const scanTypeSlugTyped: ScanTypeSlug = (scanTypeSlug || "") as ScanTypeSlug;

  const beamlineContext = useContext(BeamlineContext);

  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  const [loading, setLoading] = useState<LoadingState>(LoadingState.Loading);
  const [loadingMessage, setLoadingMessage] = useState("");


  useEffect(() => {
    if (!scanTypeSlugTyped.trim()) {
      setLoading(LoadingState.Failure);
      setLoadingMessage("Blank Scan Type slug");
      return;
    }

    if (beamlineContext.loadingState != BeamlineLoadingState.Succeeded) {
      setLoading(LoadingState.Loading);
      setLoadingMessage("");
      return;
    }

    const thisScanType = beamlineContext.scanTypes.typesBySlug.get(scanTypeSlugTyped);

    if (thisScanType === undefined) {
      setLoading(LoadingState.Failure);
      setLoadingMessage("Scan Type slug not found.");
      return;
    }
    
    setName(thisScanType.name);
    setDescription(thisScanType.description);

    setLoading(LoadingState.Success);

  }, [scanTypeSlugTyped, beamlineContext.changeCounter, beamlineContext.loadingState]);

  const scanType = beamlineContext.scanTypes.typesBySlug.get(scanTypeSlugTyped);

  // If we're in any loading state other than success, or we can't find our scan type,
  // display a loading banner instead of the content.
  if ((loading != LoadingState.Success) || !scanType) {
    return (<LoadingBanner state={loading} message={loadingMessage}></LoadingBanner>)
  }


  async function nameEditValidate(value: string) {
   const trimmed = value.toString().trim();
    if (trimmed.length < 1) {
      return { status: ValidationStatus.Failure, message: "Name cannot be blank." };
    } else if (Array.from(beamlineContext.scanTypes.typesBySlug.values()).some((c) => c.name == trimmed)) {
      return { status: ValidationStatus.Failure, message: "Name must be unique for Beamline." };
    }
    return { status: ValidationStatus.Success };
  }


  async function nameEditSubmit(value: string) {
    const oldName = scanType!.name;
    scanType!.name = value;
    const result = await updateScanType({slug: scanType!.slug, name: value});
    if (result.success) {
      setName(value);
      return { status: ValidationStatus.Success };
    } else {
      scanType!.name = oldName;
      setName(oldName);
      return { status: ValidationStatus.Failure, message: result.message };
    }
  }


  async function descriptionEditSubmit(value: string) {
    const oldDescription = scanType!.description;
    scanType!.description = value;
    const result = await updateScanType({slug: scanType!.slug, description: value});
    if (result.success) {
      setDescription(value);
      return { status: ValidationStatus.Success };
    } else {
      scanType!.description = oldDescription;
      setDescription(oldDescription);
      return { status: ValidationStatus.Failure, message: result.message };
    }
  }


  return (
    <>
      <BeamlineHeader />

      <nav className="level">
        <div className="level-left">
          <div className="level-item">
            <nav className="breadcrumb is-medium" aria-label="breadcrumbs">
              <ul>
                <li><Link to={ "/" }>Beamlines</Link></li>
                <li><Link to={ "/beamline/" + beamlineContext.beamline!.slug }>{ beamlineContext.beamline!.name }</Link></li>
                <li className="is-active"><Link to={ "/beamline/" + beamlineContext.beamline!.slug + "/scantype/" + scanTypeSlug }>{ scanType.name }</Link></li>
              </ul>
            </nav>
          </div>
          { scanType.deprecated && (
            <div className="level-item">
              <p className="subtitle is-5" style={{ "color": "darkred" }}>(This Scan Type is deprecated.)</p>
             </div>
          )}
        </div>
        <div className="level-right">
          <div className="level-item">

            <div className="dropdown is-right is-hoverable">
              <div className="dropdown-trigger">
                <button className="button" aria-haspopup="true" aria-controls="dropdown-menu-howto">
                  <FontAwesomeIcon icon={faEllipsisVertical} />
                  <span className="icon is-small">
                    <FontAwesomeIcon icon={faAngleDown} />
                  </span>
                </button>
              </div>
              <div className="dropdown-menu" id="dropdown-menu-howto" role="menu">
                <div className="dropdown-content">
                  <DeprecateScanType scantypeslug={scanTypeSlugTyped} />
                  <DeleteScanType scantypeslug={scanTypeSlugTyped} />
                </div>
              </div>
            </div>

          </div>
        </div>
      </nav>

      <div className="block">
        <InputEditable
            elementId="scantype-name"
            value={name}
            placeholder="Name of this Scan Type"
            showHelp={true}
            editFunctions={{
              validator: nameEditValidate,
              submit: nameEditSubmit
            }} />
        <InputEditable
            elementId="scantype-description"
            value={description}
            placeholder="Describe this Scan Type"
            isTextArea={true}
            useCtrlToSave={false}
            showHelp={true}
            textAreaRows={2}
            editFunctions={{
              validator: async () => { return { status: ValidationStatus.Success } },
              submit: descriptionEditSubmit
            }} />
      </div>

      <ParameterSettingsTable scantypeslug={scanTypeSlugTyped} />

    </>
  )
}

export default EditScanType
